import { Button, FormField, Paragraph, Stack, TextInput } from 'canoe';
import { Field, FieldInputProps, FieldMetaProps, Form, Formik } from 'formik';
import { compose, connectWithHammock, ENDPOINTS } from 'hammock';
import React from 'react';
import { useParams } from 'react-router';
import * as Yup from 'yup';

import { SnapshotListModal } from '@candu-ai/snapshots/components';
import { Snapshot } from '@candu-ai/snapshots/types';
import { Flags, useFlags, useNavigation, useToggle } from 'commons/hooks';
import { contentPath } from 'dashboard/pages/Content/constants';

export const validationSchema = Yup.object().shape({
  contentUrl: Yup.string()
    .min(3, 'Must have at least 3 characters')
    .matches(/^(https?:\/\/).*\..+/, 'Enter a valid URL')
    .required('Required'),
});

interface Props {
  onSkip?: () => void;
  onSubmit: (payload: object) => void;
  snapshots: Snapshot[];
}

const CreateLocationFormImpl = ({ onSubmit, onSkip, snapshots }: Props) => {
  const { [Flags.EXISTING_SNAPSHOTS]: withSnapshotChooser } = useFlags();
  const [snapshotModal, toggleSnapshotModal] = useToggle(false);
  const navigate = useNavigation();
  const params = useParams();
  const contentId = params.contentId as string;

  return (
    <>
      <Formik
        validateOnMount
        initialValues={{
          contentUrl: '',
        }}
        validationSchema={validationSchema}
        onSubmit={({ contentUrl }) => onSubmit({ contentUrl })}
      >
        {({ isSubmitting, isValid, handleSubmit }) => (
          <Form
            data-cy="create-content-location-form"
            onKeyDown={(e: KeyboardEvent) => {
              if (isValid && e.key === 'Enter') {
                handleSubmit();
              }
            }}
          >
            <Field name="contentUrl">
              {({
                field,
                meta,
              }: {
                field: FieldInputProps<string>;
                meta: FieldMetaProps<string>;
              }) => (
                <FormField
                  label="Your product URL"
                  feedback={meta.touched ? meta.error : ''}
                  state={meta.touched && meta.error ? 'danger' : 'none'}
                  labelScale="small"
                >
                  <TextInput {...field} {...meta} placeholder="your-app.io/dashboard" />
                </FormField>
              )}
            </Field>

            {withSnapshotChooser && !!snapshots?.length && (
              <Stack justify="center" space="xs" spaceAfter="l">
                <Paragraph size="short" color="OnyxGrayL10" noMargin>
                  Or
                </Paragraph>
                <Button appearance="outline" variant="secondary" onClick={toggleSnapshotModal}>
                  Select from Snapshot list
                </Button>
              </Stack>
            )}

            <Stack space="xxs" justify="center">
              <Button
                type="submit"
                variant="secondary"
                loading={isSubmitting}
                disabled={!isValid}
                mt="s"
              >
                Next
              </Button>
              {onSkip && (
                <Button appearance="text" variant="contrast" inline size="xsmall" onClick={onSkip}>
                  Skip (not recommended)
                </Button>
              )}
            </Stack>
          </Form>
        )}
      </Formik>

      {snapshotModal && (
        <SnapshotListModal
          onClose={toggleSnapshotModal}
          onSnapshotChoose={() => navigate(contentPath(contentId))}
          onSnapshotCreate={(contentUrl) => onSubmit({ contentUrl })}
          snapshots={snapshots}
        />
      )}
    </>
  );
};

const withSnapshots = connectWithHammock(() => ({
  endpoint: ENDPOINTS.snapshots,
  resourceName: 'snapshots',
  refetchInterval: false,
  refetchOnWindowFocus: false,
}));

export const CreateLocationForm = compose(withSnapshots)(CreateLocationFormImpl);
